import { useEffect, useRef, type ReactNode } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useUserProfile } from '@/hooks'
import { useUserStore, mapPlanIdToType } from '@/stores/user-store'
import { trackPurchase } from '@/services/analytics'

interface AuthSyncProviderProps {
  children: ReactNode
}

/**
 * Syncs Clerk auth state with our backend and Zustand store.
 * When a user signs in, fetches their profile from the backend
 * and updates the global store.
 */
export function AuthSyncProvider({ children }: AuthSyncProviderProps) {
  const { isLoaded: clerkLoaded, isSignedIn } = useUser()
  const { user, isLoading, creditGrant } = useUserProfile()
  const { setUser, setLoading } = useUserStore()
  const hasTrackedPurchaseRef = useRef(false)

  // Sync user data to store when it changes
  useEffect(() => {
    if (!clerkLoaded) {
      setLoading(true)
      return
    }

    if (!isSignedIn) {
      setUser(null)
      setLoading(false)
      return
    }

    if (user) {
      setUser(user)
      setLoading(false)
      
      // Track purchase event for paid users who haven't had it tracked yet
      // This fires the GTM purchase event for Meta attribution
      const planType = mapPlanIdToType(user.plan_id)
      if (
        planType !== 'free' &&
        !user.meta_purchase_event_sent &&
        !hasTrackedPurchaseRef.current
      ) {
        hasTrackedPurchaseRef.current = true
        trackPurchase(
          user.user_id,
          user.plan_id,
          'subscription_sync',
          user.email,
          user.meta_purchase_event_sent
        )
      }
      
      // Show notification if credits were granted
      if (creditGrant) {
        // TODO: Show toast notification
        console.log(`Credit grant: ${creditGrant.credits_awarded} credits - ${creditGrant.reason}`)
      }
    } else if (!isLoading) {
      setLoading(false)
    }
  }, [clerkLoaded, isSignedIn, user, isLoading, creditGrant, setUser, setLoading])

  return <>{children}</>
}
